import nprogress from 'nprogress';
import queryString from 'query-string';
import type { match as ReactRouterMatch } from 'react-router-dom';
import { all, put, race } from 'redux-saga/effects';

import type { PartFileStandaloneViewerData } from '@frontend/api';
import { fileProcessingService, FileProcessingState } from '@frontend/services';

import { viewer } from 'modules/common/viewer/data/actions';

import partService from 'services/partService';

import { fileViewer } from './actions';

// TODO: manage polling and/or websocket connection?
export function* initFileViewer({
  location,
}: {
  location: Location;
  match: ReactRouterMatch<Record<string, string>>;
}) {
  const { file } = queryString.parse(location.search);
  const activePartFileUuid = typeof file === 'string' ? file : '';
  try {
    yield put(viewer.reset());

    let activeFile: PartFileStandaloneViewerData;

    let intervalId: ReturnType<typeof setInterval> | undefined;

    const firstRequests = {
      isProcessed: new Promise<boolean>((resolve) => {
        if (fileProcessingService.getIsProcessed(activePartFileUuid)) {
          resolve(true);
        } else {
          const { unsubscribe } = fileProcessingService.subscribe({
            partFileUuid: activePartFileUuid,
            fileProcessingStateSetter: (v?: FileProcessingState) => {
              if (v === FileProcessingState.PROCESSED) {
                resolve(true);
                unsubscribe();
              } else {
                if (!intervalId) {
                  //nprogress tracks progress of fetch(), and we're making multiple requests
                  //to check the status. This setInterval resets the progress every second until
                  //the backend is finished processing the file.
                  //when isProcessed is true, we'll nprogress.start() is called to restart watching the
                  //fetch of activeFile.
                  intervalId = setInterval(() => {
                    nprogress.set(0.1);
                  }, 1000);
                }
              }
            },
          });
        }
      }),
      activeFile: partService.partFiles.fileViewer.get(activePartFileUuid),
    };

    const { isProcessed, activeFile: firstActiveFileRequest } = yield race(
      firstRequests,
    );
    if (isProcessed) {
      //wait for the firstRequests.activeFile to complete because we know it is processed
      activeFile = yield firstRequests.activeFile;
    } else if (firstActiveFileRequest && firstActiveFileRequest.hasGeometry) {
      //We know the first request's result is good because it has geometry
      activeFile = firstActiveFileRequest;
    } else {
      //The first request may be good, but we don't know for sure because if not processed, then
      //the reason for not having geometry could be simply that it is not processed yet.
      //So wait until we know it is processed and refetch so we know we have the fully processed file
      //Then we know we can trust `activeFile.hasGeometry` (whether true or false).
      yield firstRequests.isProcessed;
      nprogress.start(); //restart
      activeFile = yield partService.partFiles.fileViewer.get(
        activePartFileUuid,
      );
      if (intervalId) {
        clearInterval(intervalId);
      }
    }

    // NOTE: we are using the plural viewer.receivePartFiles here because that has side effects that manage loading
    //  the singular recieve part file does not have loading side effects...maybe it should?
    yield all([
      put(viewer.receivePartFiles({ partFiles: [activeFile] })),
      put(
        viewer.receiveActivePartFileUuid({
          partFileUuid: activePartFileUuid,
          shouldFocus: true,
          shouldPropagateVisibility: true,
        }),
      ),
      put(fileViewer.init({ activeRootFile: activeFile })),
    ]);
  } catch (error) {
    console.error(error);
  }
}
